import React, { useMemo, useEffect } from 'react';
import { useMetabolicStore } from '../../stores/v4/useMetabolicStore';
import { useUIStore } from '@stores/v4/useUIStore';
import { useAthleteStore } from '../../stores/v4/useAthleteStore';
import { useHistoryStore } from '../../stores/v4/useHistoryStore';
import { Box, Typography, Pressable } from '@/shared/design-system/primitives';
import { formatMacro } from '@shared/utils/formatting';
import { ChevronDown, ChevronRight, Activity, Zap, AlertTriangle } from 'lucide-react';
import { useHubUIStore } from '../../stores/v4/useHubUIStore';
import { useMetabolicMatrix } from './hooks/useMetabolicMatrix';
import { ALL_PILLAR_KEYS, normalizeNutrientKey, NUTRIENT_REGISTRY } from '@/shared/constants/metabolic-pillars';

// Atomic Sub-Components
import { EnergyCommandCard } from './components/EnergyCommandCard';
import { PillarSystemCard } from './components/PillarSystemCard';
import { OtherCompoundsCard } from './components/OtherCompoundsCard';
import { InsulinSpikeTimeline } from './components/InsulinSpikeTimeline';
import { MacronutrientTimeline } from './components/MacronutrientTimeline';
import { CoachAdviceCard } from './components/CoachAdviceCard';
import { WeightWarningBanner } from './components/WeightWarningBanner';
import { MetabolicEngineToggle } from './components/MetabolicEngineToggle';
import { SystemicPhaseTimeline } from './components/SystemicPhaseTimeline';

import { toLogicDate } from '@/utils/date-utils';
import { useConfigStore } from '../../stores/v4/useConfigStore';
import '@/apps/shared-premium.css';

/**
 * MetabolicHubView: High-fidelity metabolic simulation dashboard.
 * V7.6: Optimized data-sync and added dedicated 'Other' section.
 */
export const MetabolicHubView: React.FC = () => {
    const { dailySnapshot, coachAdvice, isCoachAnalyzing, calculateDailyBalance, fetchCoachAdvice, weightWarning } = useMetabolicStore();
    const { lastSelectedDate } = useUIStore();
    const { profile } = useAthleteStore();
    const { events, getEventsForDateRange } = useHistoryStore();
    const { engineMode, setEngineMode, showDailyLedger, showLiveFuelGauge, showGlycogenSink } = useConfigStore();

    const isInsulinCollapsed = useHubUIStore(state => state.collapsedSections['insulin-engine'] || false);
    const isMacroCollapsed = useHubUIStore(state => state.collapsedSections['macro-timeline'] || false);
    const toggleSection = useHubUIStore(state => state.toggleSection);

    const selectedDate = useMemo(() => lastSelectedDate ? new Date(lastSelectedDate) : new Date(), [lastSelectedDate]);
    const { expended, nutrition, supplements, combined, balance, targets, dayEvents, currentActiveFuel, glycogenCapacity, timeToDepletionMins } = dailySnapshot;

    // The Hub Aggregates both for the Physiological Matrix
    const { thresholds, pillarData, sensitivityLabel } = useMetabolicMatrix(profile, combined, targets);

    // Identify OTHER compounds for the Hub grid (Normalized)
    const otherMetrics = useMemo(() => {
        const micros = supplements.micros || {};
        const blacklist = [...ALL_PILLAR_KEYS, 'discovered_micros', 'detailed_metrics'];
        
        return Object.keys(micros)
            .filter(k => {
                const canonicalKey = normalizeNutrientKey(k);
                return !blacklist.includes(canonicalKey) && (micros[k].total || 0) > 0;
            })
            .map(k => {
                const lowKey = normalizeNutrientKey(k);
                return {
                    id: k,
                    label: NUTRIENT_REGISTRY[lowKey]?.label || k.replace(/_/g, ' ').toUpperCase(),
                    value: micros[k].total,
                    target: 0,
                    unit: micros[k].unit || NUTRIENT_REGISTRY[lowKey]?.unit || 'mg',
                    sources: micros[k].sources
                };
            });
    }, [supplements.micros]);

    const fatBurnStats = useMemo(() => {
        if (!dailySnapshot.simulation || dailySnapshot.simulation.length === 0) return { display: '24h 00m', isLow: false };

        const stablePoints = dailySnapshot.simulation.filter((p: any) => p.state.insulin < (thresholds.stable / 4)).length;
        const totalMinutes = stablePoints * 5; 
        return { 
            display: `${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}m`,
            isLow: totalMinutes < 720
        };
    }, [dailySnapshot.simulation, thresholds]);

    // REACTIVE SYNC: Recalculate whenever selected date, history, or profile changes
    useEffect(() => {
        calculateDailyBalance(selectedDate, events);
    }, [selectedDate, events, calculateDailyBalance, profile]);

    const numericStyle = { fontVariantNumeric: 'lining-nums', fontFamily: 'ui-sans-serif, system-ui, sans-serif' };

    const isToday = toLogicDate(selectedDate) === toLogicDate(new Date());

    return (
        <Box style={{ flex: 1 }}>
            <WeightWarningBanner isVisible={weightWarning} />

            <MetabolicEngineToggle />

            {showDailyLedger !== false && (
                <EnergyCommandCard balance={balance} consumed={nutrition} expended={expended} />
            )}

            {/* SYSTEMIC METABOLIC PHASE (The 0-100 Dial) */}
            {dailySnapshot.systemicPhase && (
                <Box style={{ padding: '20px', backgroundColor: '#020202', borderBottomWidth: 1, borderBottomColor: '#111' }}>
                    <Box style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
                        <Typography style={{ fontSize: 9, fontWeight: '900', color: '#666', letterSpacing: 2, textTransform: 'uppercase' }}>Systemic Metabolic Phase</Typography>
                        <Typography style={{ fontSize: 9, fontWeight: '900', color: '#888' }}>
                            {dailySnapshot.systemicPhase.hoursFasted.toFixed(1)}h Fasted
                        </Typography>
                    </Box>

                    {/* THE DIAL (Linear Speedometer) */}
                    <Box style={{ marginBottom: 20 }}>
                        <Box style={{ height: 12, backgroundColor: '#111', borderRadius: 6, flexDirection: 'row', overflow: 'hidden', borderWidth: 1, borderColor: '#222' }}>
                            <Box style={{ flex: 20, backgroundColor: '#ef4444', opacity: 0.3 }} /><Box style={{ flex: 20, backgroundColor: '#2ecc71', opacity: 0.3 }} /><Box style={{ flex: 20, backgroundColor: '#eab308', opacity: 0.3 }} /><Box style={{ flex: 20, backgroundColor: '#f97316', opacity: 0.3 }} /><Box style={{ flex: 10, backgroundColor: '#a855f7', opacity: 0.3 }} /><Box style={{ flex: 10, backgroundColor: '#3b82f6', opacity: 0.3 }} />
                        </Box>
                        
                        {/* THE NEEDLE */}
                        <Box style={{ 
                            position: 'absolute', 
                            top: -4, 
                            left: `${dailySnapshot.systemicPhase.score}%`, 
                            marginLeft: -1,
                            width: 2, 
                            height: 20, 
                            backgroundColor: '#fff',
                            boxShadow: '0 0 10px rgba(255,255,255,0.5)',
                            transition: 'left 0.5s ease-out'
                        }}>
                            <Box style={{ 
                                position: 'absolute', 
                                top: -8, 
                                left: -4, 
                                width: 10, 
                                height: 10, 
                                borderRadius: 5, 
                                backgroundColor: dailySnapshot.systemicPhase.color,
                                borderWidth: 2,
                                borderColor: '#fff'
                            }} />
                        </Box>
                    </Box>

                    <Box style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography style={{ 
                            fontSize: 16, 
                            fontWeight: '950', 
                            color: dailySnapshot.systemicPhase.color, 
                            letterSpacing: 1,
                            textAlign: 'center'
                        }}>
                            {dailySnapshot.systemicPhase.label}
                        </Typography>
                    </Box>
                </Box>
            )}

            {/* LIVE FUEL GAUGE (Visible on all days) */}
            {showLiveFuelGauge !== false && currentActiveFuel && (
                <Box style={{ padding: '20px', backgroundColor: '#050505', borderBottomWidth: 1, borderBottomColor: '#111' }}>
                    <Box style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 15 }}>
                        <Box>
                            <Typography style={{ ...numericStyle, fontSize: 24, fontWeight: '900', color: '#f29b11' }}>{currentActiveFuel.carbs.toFixed(0)}<span style={{ fontSize: 12, color: '#888' }}>g</span></Typography>
                            <Typography style={{ fontSize: 8, fontWeight: '900', color: '#666', textTransform: 'uppercase', letterSpacing: 1 }}>Blood Carbs</Typography>
                        </Box>
                        <Box>
                            <Typography style={{ ...numericStyle, fontSize: 24, fontWeight: '900', color: '#3b82f6' }}>{currentActiveFuel.protein.toFixed(0)}<span style={{ fontSize: 12, color: '#888' }}>g</span></Typography>
                            <Typography style={{ fontSize: 8, fontWeight: '900', color: '#666', textTransform: 'uppercase', letterSpacing: 1 }}>Blood Aminos</Typography>
                        </Box>
                        <Box>
                            <Typography style={{ ...numericStyle, fontSize: 24, fontWeight: '900', color: '#ef4444' }}>{currentActiveFuel.fat.toFixed(0)}<span style={{ fontSize: 12, color: '#888' }}>g</span></Typography>
                            <Typography style={{ fontSize: 8, fontWeight: '900', color: '#666', textTransform: 'uppercase', letterSpacing: 1 }}>Blood Lipids</Typography>
                        </Box>
                        <Box>
                            <Typography style={{ ...numericStyle, fontSize: 24, fontWeight: '900', color: '#555' }}>{currentActiveFuel.gastricLoad.toFixed(0)}<span style={{ fontSize: 12, color: '#888' }}>g</span></Typography>
                            <Typography style={{ fontSize: 8, fontWeight: '900', color: '#666', textTransform: 'uppercase', letterSpacing: 1 }}>Stomach Load</Typography>
                        </Box>
                    </Box>
                    <Box style={{ backgroundColor: '#111', padding: 12, borderRadius: 8, borderWidth: 1, borderColor: '#222' }}>
                        <Typography style={{ fontSize: 10, fontWeight: '800', color: '#aaa' }}>
                            Estimated Depletion: <span style={{ color: '#fff', fontWeight: '900' }}>{Math.floor(timeToDepletionMins / 60)}h {timeToDepletionMins % 60}m</span>
                        </Typography>
                    </Box>
                </Box>
            )}

            {/* GLYCOGEN SINK */}
            {showGlycogenSink !== false && glycogenCapacity && (
                <Box style={{ padding: '20px', backgroundColor: '#020202', borderBottomWidth: 1, borderBottomColor: '#111' }}>
                    <Box style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 10 }}>
                        <Typography style={{ fontSize: 9, fontWeight: '900', color: '#666', letterSpacing: 2, textTransform: 'uppercase' }}>Muscle Glycogen Sink</Typography>
                        <Typography style={{ ...numericStyle, fontSize: 14, fontWeight: '900', color: glycogenCapacity.spilloverRisk ? '#ef4444' : '#2ecc71' }}>
                            {glycogenCapacity.current.toFixed(0)} / {glycogenCapacity.max}g
                        </Typography>
                    </Box>
                    <Box style={{ height: 6, backgroundColor: '#111', borderRadius: 3, overflow: 'hidden' }}>
                        <Box style={{ 
                            height: '100%', 
                            width: `${Math.min(100, (glycogenCapacity.current / glycogenCapacity.max) * 100)}%`, 
                            backgroundColor: glycogenCapacity.spilloverRisk ? '#ef4444' : '#2ecc71',
                            transition: 'width 0.5s ease-out'
                        }} />
                    </Box>
                    {glycogenCapacity.spilloverRisk && (
                        <Typography style={{ fontSize: 9, fontWeight: '800', color: '#ef4444', marginTop: 8 }}>
                            WARNING: Sink is at maximum capacity. Additional carbs will route to De Novo Lipogenesis (Fat Storage).
                        </Typography>
                    )}
                </Box>
            )}

            {/* 2. INSULIN KINETIC ENGINE */}
            <Box style={{ 
                backgroundColor: 'rgba(255,255,255,0.02)', borderBottomWidth: 1, 
                borderBottomColor: isInsulinCollapsed ? 'rgba(255,255,255,0.05)' : 'rgba(242, 155, 17, 0.2)',
                marginBottom: 15, overflow: 'hidden'
            }}>
                <Pressable onPress={() => toggleSection('insulin-engine')} style={{ padding: 20, display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                        <Activity size={14} color="#06b6d4" />
                        <Typography style={{ fontSize: 11, fontWeight: '950', color: '#06b6d4', letterSpacing: 1.5 }}>INSULIN KINETIC ENGINE</Typography>
                    </Box>
                    <Box style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                        {isInsulinCollapsed && (
                            <Typography style={{ ...numericStyle, fontSize: 16, fontWeight: '950', color: nutrition.insulin_load.total > thresholds.efficient ? '#ef4444' : '#2ecc71', marginTop: 1 }}>
                                {formatMacro(nutrition.insulin_load.total)}
                            </Typography>
                        )}
                        {isInsulinCollapsed ? <ChevronRight size={16} color="#444" /> : <ChevronDown size={16} color="#06b6d4" />}
                    </Box>
                </Pressable>

                {!isInsulinCollapsed && (
                    <Box style={{ paddingHorizontal: 20, paddingBottom: 20 }}>
                        <Box style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', marginBottom: 10, alignItems: 'center' }}>
                            <Box style={{ display: 'flex', flexDirection: 'row', alignItems: 'baseline', gap: 8 }}>
                                <Typography style={{ ...numericStyle, fontSize: 22, fontWeight: '900', color: nutrition.insulin_load.total > thresholds.efficient ? '#e74c3c' : '#2ecc71' }}>{formatMacro(nutrition.insulin_load.total)}</Typography>
                                <Typography style={{ fontSize: 10, fontWeight: '900', color: nutrition.insulin_load.total > thresholds.efficient ? '#e74c3c' : '#2ecc71' }}>{nutrition.insulin_load.total > thresholds.efficient ? 'INFLAMMATORY' : 'EFFICIENT'}</Typography>
                            </Box>
                            <Typography style={{ fontSize: 9, fontWeight: '900', color: '#444', textTransform: 'uppercase' }}>{sensitivityLabel} SENSITIVITY</Typography>
                        </Box>
                        <InsulinSpikeTimeline targetDate={selectedDate} sensitivity={profile?.insulin_sensitivity || 'NORMAL'} consumed={nutrition} thresholds={thresholds} />
                    </Box>
                )}
            </Box>

            {/* 2.5 MACRONUTRIENT DISPERSION TIMELINE */}
            <Box style={{ 
                backgroundColor: 'rgba(255,255,255,0.02)', borderBottomWidth: 1, 
                borderBottomColor: isMacroCollapsed ? 'rgba(255,255,255,0.05)' : 'rgba(59, 130, 246, 0.2)',
                marginBottom: 15, overflow: 'hidden'
            }}>
                <Pressable onPress={() => toggleSection('macro-timeline')} style={{ padding: 20, display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                        <Activity size={14} color="#3b82f6" />
                        <Typography style={{ fontSize: 11, fontWeight: '950', color: '#3b82f6', letterSpacing: 1.5 }}>MACRONUTRIENT DISPERSION</Typography>
                    </Box>
                    <Box style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                        {isMacroCollapsed ? <ChevronRight size={16} color="#444" /> : <ChevronDown size={16} color="#3b82f6" />}
                    </Box>
                </Pressable>

                {!isMacroCollapsed && (
                    <Box style={{ paddingHorizontal: 20, paddingBottom: 20 }}>
                        <MacronutrientTimeline targetDate={selectedDate} events={events} />
                        <SystemicPhaseTimeline simulation={dailySnapshot.simulation} />
                    </Box>
                )}
            </Box>

            <Box style={{ marginBottom: 10 }}>
                <Typography style={{ fontSize: 9, fontWeight: '900', color: '#444', letterSpacing: 2, marginBottom: 12, marginLeft: 20 }}>PHYSIOLOGICAL MATRIX</Typography>
                {pillarData.map(pillar => (
                    <PillarSystemCard 
                        key={pillar.title} 
                        title={pillar.title} 
                        color={pillar.color} 
                        metrics={pillar.metrics} 
                    />
                ))}

                {/* 2.5 OTHER ACTIVE COMPOUNDS SECTION */}
                {otherMetrics.length > 0 && (
                    <OtherCompoundsCard 
                        metrics={otherMetrics}
                    />
                )}
            </Box>

            <Box style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 20, backgroundColor: 'rgba(46, 204, 113, 0.05)', borderBottomWidth: 1, borderBottomColor: 'rgba(46, 204, 113, 0.1)', marginBottom: 15 }}>
                <Box style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}><Zap size={14} color="#2ecc71" /><Typography style={{ fontSize: 10, fontWeight: '950', color: '#2ecc71', letterSpacing: 1.5 }}>FAT OXIDATION WINDOW</Typography></Box>
                <Typography style={{ ...numericStyle, fontSize: 18, fontWeight: '900', color: '#fff', marginTop: 1 }}>{fatBurnStats.display}</Typography>
            </Box>

            <CoachAdviceCard coachAdvice={coachAdvice} isCoachAnalyzing={isCoachAnalyzing} onRefresh={() => fetchCoachAdvice()} />
        </Box>
    );
};
